import {captureRemixErrorBoundaryError, withSentry} from "@sentry/remix"
import {
  isRouteErrorResponse,
  Link,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useRouteError,
} from "@remix-run/react"
import {Prism as SyntaxHighlighter} from "react-syntax-highlighter"
import styles from "./tailwind.css?url"
import reactflowStyles from "@xyflow/react/dist/style.css?url"
import lilyScriptFont from '@fontsource/lily-script-one/index.css?url'
import rethinkFont from '@fontsource-variable/rethink-sans/index.css?url'
import {supabaseAnonKey, supabaseUrl} from "~/.server/supabase/constants.server"
import {SupabaseContextProvider} from "./contexts/useSupabaseContext"
import {json, LoaderFunctionArgs} from "@remix-run/node"
import {createBrowserClient} from "@supabase/ssr"
import {Database} from "./generated/supabase"
import {
  GetFeaturedArticlesQuery,
  GetFeaturedArticlesQueryResult
} from "~/.server/article/getFeaturedArticlesQuery.server"
import {fetchFromGraphQL} from "~/.server/utils/fetchFromGraphQL.server"
import {FeaturedArticlesContextProvider} from "~/contexts/useFeaturedArticlesContext"
import {createServerContext} from "~/.server/supabase/createServerContext.server"
import {UserContextProvider} from "~/contexts/useUserContext"
import {PageLoader} from "~/components/PageLoader"
import {ToolbarContextProvider} from "~/contexts/useToolbarContext"
import {Analytics} from "@vercel/analytics/react"
import clsx from "clsx"
import {LogoIcon} from "~/components/icons/LogoIcon"
import {coldarkDark} from "react-syntax-highlighter/dist/cjs/styles/prism"
import {FeatureFlagsContextProvider} from "~/contexts/useFeatureFlagsContext"
import {getFeatureFlags} from "~/.server/profile/getFeatureFlags"
import {DefaultFeatureFlags, FeatureFlags} from "~/.shared/featureFlags/FeatureFlags"
import {ServerConstants} from "~/.server/utils/ServerConstants.server"
import {AnalyticsContextProvider} from "~/contexts/useAnalyticsContext"
import {Toaster} from "react-hot-toast"
import {getModelConfigs, LoadedModelConfigs} from "~/.server/languageModel/getModelConfigs"
import {withFallback} from "~/.server/utils/withFallback.server"
import {ModelConfigsContextProvider} from "~/contexts/useModelConfigsContext"

export async function loader({request}: LoaderFunctionArgs) {

  // Everything here falls back to an empty value on failure or timeout, so an outage in Supabase or Hygraph
  // degrades the page instead of taking down every route. Routes that need this data must check for it themselves.
  const context = await withFallback("createServerContext", createServerContext(request), null)
  const supabase = context?.supabase
  const profile = context?.profile ?? null

  const [featureFlags, featuredArticlesResponse, {modelConfigs, providerConfigs}] = await Promise.all([
    supabase && profile?.id
      ? withFallback(
        "getFeatureFlags",
        getFeatureFlags({userId: profile.id, supabase})
          .then(({data}) => data?.feature_flags as FeatureFlags | undefined ?? DefaultFeatureFlags),
        DefaultFeatureFlags
      )
      : DefaultFeatureFlags,
    withFallback(
      "GetFeaturedArticlesQuery",
      fetchFromGraphQL<GetFeaturedArticlesQueryResult, any>(GetFeaturedArticlesQuery),
      undefined
    ),
    supabase
      ? withFallback("getModelConfigs", getModelConfigs({supabase}), EmptyModelConfigs)
      : EmptyModelConfigs,
  ])

  return json({
    env: {
      NodeEnvironment: ServerConstants.Environment.NodeEnvironment,
      SegmentWriteKey: ServerConstants.Environment.SegmentWriteKey,
      sentryDSN: ServerConstants.Environment.sentryDSN,
    },
    supabaseAnonKey,
    supabaseUrl,
    profile,
    featureFlags,
    featuredArticles: featuredArticlesResponse?.data?.articles,
    errors: featuredArticlesResponse?.errors,
    modelConfigs,
    providerConfigs,
  }, {
    headers: context?.getHeaders()
  })
}

const EmptyModelConfigs: LoadedModelConfigs = {
  modelConfigs: [],
  providerConfigs: [],
}

export const meta = () => {
  return [
    {title: "Ben De La Haye - Principal Product Manager"},
  ]
}

export function links() {
  return [
    {
      rel: "stylesheet",
      href: lilyScriptFont
    },
    {
      rel: "stylesheet",
      href: rethinkFont
    },
    {
      rel: "stylesheet",
      href: reactflowStyles
    },
    {
      rel: "stylesheet",
      href: styles
    },
  ]
}

function App() {

  const {
    env,
    supabaseAnonKey,
    supabaseUrl,
    profile,
    featureFlags,
    featuredArticles,
    modelConfigs,
    providerConfigs,
  } = useLoaderData<typeof loader>()

  const supabase = createBrowserClient<Database>(
    supabaseUrl,
    supabaseAnonKey
  )

  return (
    <AnalyticsContextProvider writeKey={env.SegmentWriteKey} profile={profile}>
      <SupabaseContextProvider supabase={supabase}>
        <FeatureFlagsContextProvider featureFlags={featureFlags}>
          <UserContextProvider profile={profile ?? null}>
            <FeaturedArticlesContextProvider featuredArticles={featuredArticles ?? []}>
              <ModelConfigsContextProvider modelConfigs={modelConfigs} providerConfigs={providerConfigs}>
              <ToolbarContextProvider>
                <html lang="en">
                <head>
                  <meta charSet="utf-8"/>
                  <meta name="viewport" content="width=device-width, initial-scale=1"/>
                  <Meta/>
                  <Links/>
                </head>
                <body>
                <div className="h-dvh min-h-dvh flex flex-col">
                  <PageLoader/>
                  <Outlet/>
                  <Toaster
                    containerClassName=""
                    position="bottom-right"
                  />
                </div>
                <ScrollRestoration/>
                <script
                  dangerouslySetInnerHTML={{
                    __html: `window.env = ${JSON.stringify(env)}`,
                  }}
                />
                <Scripts/>
                <Analytics/>
                </body>
                </html>
              </ToolbarContextProvider>
              </ModelConfigsContextProvider>
            </FeaturedArticlesContextProvider>
          </UserContextProvider>
        </FeatureFlagsContextProvider>
      </SupabaseContextProvider>
    </AnalyticsContextProvider>
  )
}

export default withSentry(App)


export function ErrorBoundary() {

  const response = useRouteError()
  captureRemixErrorBoundaryError(response)

  const title = isRouteErrorResponse(response)
    ? `${response.status}${response.statusText ? ` - ${response.statusText}` : ""}`
    : (response instanceof Error) ? "An error occurred"
      : "An unknown error occurred"

  // Stack traces are only shown in development, visitors get a generic message
  const errorMessage = isRouteErrorResponse(response) ?
    response.data?.["message"] ?? JSON.stringify(response.data, null, 2)
    : (response instanceof Error)
      ? import.meta.env.DEV
        ? response.stack ?? response.message ?? ""
        : "Something went wrong. Please try again later."
      : undefined

  return (
    <html lang="en">
    <head>
      <meta charSet="utf-8"/>
      <meta name="viewport" content="width=device-width, initial-scale=1"/>
      <Meta/>
      <Links/>
    </head>
    <body>
    <div className="h-dvh min-h-dvh flex flex-col">
      <PageLoader/>
      <div
        className={clsx(
          "flex-shrink-0 w-full h-14 flex items-center px-1 sm:h-16 sm:px-3",
          "border-b border-gray-300"
        )}
      >
        <Link
          className="flex px-1 rounded-full hover:bg-gray-100"
          to="/"
        >
          <LogoIcon
            className="h-12 w-12 rounded-full"
          />
        </Link>
      </div>
      <div className="m-auto flex-grow flex flex-col justify-center py-4 sm:w-152 sm:px-2">
        <div className="mb-10 border border-gray-300 p-8 rounded-lg shadow-sm">
          <h1 className="my-4 text-3xl text-black text-center capitalize">
            {title}
          </h1>
          <div className="p-4">
            <SyntaxHighlighter
              className="rounded-lg"
              language="json"
              style={coldarkDark}
            >
              {errorMessage}
            </SyntaxHighlighter>
          </div>
        </div>
      </div>
    </div>
    <ScrollRestoration/>
    <Scripts/>
    <Analytics/>
    </body>
    </html>
  )
}